'use client';
import React, { useState, createContext, useContext } from 'react';
import Sidebar from './Sidebar';
import TopStatusBar from './TopStatusBar';

export type Language = 'EN' | 'BM';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (en: string, bm: string) => string;
}

export const LanguageContext = createContext<LanguageContextType>({
  language: 'EN',
  setLanguage: () => {},
  t: (en) => en,
});

export function useLanguage() {
  return useContext(LanguageContext);
}

interface AppLayoutProps {
  children: React.ReactNode;
  activeRoute?: string;
}

export default function AppLayout({ children, activeRoute }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [language, setLanguage] = useState<Language>('EN');

  const t = (en: string, bm: string) => language === 'BM' ? bm : en;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      <div className="flex h-screen overflow-hidden" style={{ background: 'var(--background)' }}>
        {/* Desktop sidebar — hidden on mobile */}
        <div className="hidden md:flex flex-shrink-0">
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed((v) => !v)}
            activeRoute={activeRoute}
            language={language}
          />
        </div>

        {/* Mobile drawer overlay */}
        {mobileDrawerOpen && (
          <div className="fixed inset-0 z-50 md:hidden flex">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileDrawerOpen(false)}
            />
            <div className="relative z-10 flex-shrink-0">
              <Sidebar
                collapsed={false}
                onToggle={() => setMobileDrawerOpen(false)}
                activeRoute={activeRoute}
                language={language}
                onNavClick={() => setMobileDrawerOpen(false)}
              />
            </div>
          </div>
        )}

        <div className="flex flex-col flex-1 min-w-0 overflow-hidden transition-all duration-300">
          <TopStatusBar
            language={language}
            onLanguageChange={setLanguage}
            onMobileMenuToggle={() => setMobileDrawerOpen(v => !v)}
          />
          <main className="flex-1 overflow-auto scrollbar-thin">
            {children}
          </main>
        </div>
      </div>
    </LanguageContext.Provider>
  );
}